import React, { useEffect, useState } from 'react';
import { usePartnerTeamRolesQuery } from '@hooks/requests/queries/use_partner_team_roles_query';
import { useEditRoleMutation } from '@hooks/requests/queries/use_edit_role_mutation';
import { useToast } from '@hooks/useToast';
import { getDisplayRole } from '@hooks/usePartnerRoles';
import { ApiError, describeMutationError } from '@hooks/requests/utils/_partner_api_client';
import type { DashboardMember } from '@hooks/requests/queries/use_partner_team_query';
import { Modal, Button, Alert, DropdownFilter } from '../ui';

interface UpdateRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: DashboardMember | null;
}

/** "PartnerAdmin" -> "Admin", "AceUpViewer" -> "Viewer". */
const roleLabel = (roleName: string): string => {
  if (roleName.endsWith('Admin')) return 'Admin';
  if (roleName.endsWith('Viewer')) return 'Viewer';
  return roleName;
};

const memberLabel = (member: DashboardMember): string =>
  member.name || member.email || member.userId;

const isPartiallyApplied = (error: unknown): boolean =>
  error instanceof ApiError &&
  error.status === 502 &&
  Boolean((error.body as { partiallyApplied?: boolean } | undefined)?.partiallyApplied);

/**
 * Update role modal (FR-010/011/012/013/014/022).
 *
 * `member` carries the row being edited; the dropdown's "current role" comes
 * from it directly rather than a fresh lookup, since `TeamTable` already had
 * it to render the row that opened this modal.
 */
const UpdateRoleModal: React.FC<UpdateRoleModalProps> = ({ isOpen, onClose, member }) => {
  const currentRole = member ? getDisplayRole(member.roles) : null;
  const [role, setRole] = useState('');
  const [error, setError] = useState('');

  const rolesQuery = usePartnerTeamRolesQuery({ enabled: isOpen });
  const editRoleMutation = useEditRoleMutation();
  const { showToast } = useToast();

  const roles = rolesQuery.data ?? [];

  // Re-sync to the row's current role every time the modal opens (FR-010) —
  // a prior member's selection never leaks into the next one.
  useEffect(() => {
    if (!isOpen) return;
    setRole(currentRole ?? '');
    setError('');
  }, [isOpen, currentRole]);

  const handleClose = () => {
    onClose();
  };

  const rolesReady = !rolesQuery.isLoading && !rolesQuery.isError;
  const hasChanged = Boolean(role) && role !== currentRole;
  const canSubmit = hasChanged && rolesReady;

  const handleSubmit = async () => {
    if (!canSubmit || !member) return;
    setError('');

    try {
      await editRoleMutation.mutateAsync({ memberId: member.userId, role });
      showToast(`${memberLabel(member)}'s role updated to ${roleLabel(role)}`);
      handleClose();
    } catch (err) {
      if (isPartiallyApplied(err)) {
        setError(
          `The change was only partly applied — ${memberLabel(member)} currently holds both roles. Please try again.`
        );
        return;
      }
      setError(
        describeMutationError(err, {
          notFound: `${memberLabel(member)} no longer exists in this organization.`,
          fallback: 'Failed to update the role',
        })
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit role"
      headerVariant="tinted"
      className="api-keys-modal account-role-modal"
      bodyClassName="api-keys-modal__body"
      footerClassName="api-keys-modal__foot"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" className="au-modal-pill-btn--cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="au-modal-pill-btn--confirm"
            onClick={handleSubmit}
            isLoading={editRoleMutation.isPending}
            disabled={!canSubmit}
          >
            Save changes
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {member && (
          <div className="au-modal-context-line">
            Update role for <span className="font-semibold">{memberLabel(member)}</span>.
          </div>
        )}

        <div>
          <span className="api-keys-value-label block">Role</span>
          {rolesQuery.isLoading && (
            <p className="account-modal-note" data-testid="edit-role-loading">
              Loading roles…
            </p>
          )}
          {rolesQuery.isError && (
            <p
              className="account-modal-note account-modal-note--error"
              data-testid="edit-role-error"
            >
              Unable to load roles right now.
            </p>
          )}
          {rolesReady && (
            // Same single-select DropdownFilter as the User Analytics "Org" filter.
            <div className="account-role-dropdown" data-testid="role-dropdown">
              <DropdownFilter
                name="role"
                required
                options={roles.map((candidate) => ({
                  label: roleLabel(candidate.name),
                  value: candidate.name,
                }))}
                value={role}
                onChange={(_name, value) => {
                  const next = Array.isArray(value) ? value[0] : value;
                  if (next) setRole(next);
                }}
              />
            </div>
          )}
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
      </div>
    </Modal>
  );
};

export default UpdateRoleModal;
